import React from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  CloseIcon,
  SearchIcon,
} from "../Icons/Icons";

// Pieces shared by the redesigned tables (styles in DataTable.css)

type Order = 'asc' | 'desc';

// Sortable column heading: the label plus an arrow for the current direction
export const SortButton = ({label, active, order, onClick}: {
  label: React.ReactNode;
  active: boolean;
  order: Order;
  onClick: () => void;
}) => (
  <button type="button" className={`dt-sort ${active ? 'active' : ''}`} onClick={onClick}>
    {label}
    {active && order === 'desc' ? <ChevronDownIcon/> : <ChevronUpIcon/>}
  </button>
);

// Footer: "Showing x of y" and the pager (hidden when there's only one page)
export const DataTableFoot = ({shown, total, noun, page, totalPages, loading, onPage}: {
  shown: number;
  total: number;
  noun: string;
  page: number;
  totalPages: number;
  loading: boolean;
  onPage: (page: number) => void;
}) => (
  <div className="dt-foot">
    <span className="dt-showing">
      Showing {loading ? '…' : shown} of {total.toLocaleString()} {noun}
    </span>

    {totalPages > 1 && (
      <nav className="dt-pager" aria-label="Pages">
        <button type="button" className="btn btn-sm icon-btn" disabled={page <= 1 || loading} onClick={()=>onPage(1)} aria-label="First page">
          <ChevronsLeftIcon/>
        </button>
        <button type="button" className="btn btn-sm" disabled={page <= 1 || loading} onClick={()=>onPage(page - 1)}>
          <ChevronLeftIcon/> Previous
        </button>
        <span className="dt-pager-status">Page {page} of {totalPages}</span>
        <button type="button" className="btn btn-sm" disabled={page >= totalPages || loading} onClick={()=>onPage(page + 1)}>
          Next <ChevronRightIcon/>
        </button>
        <button type="button" className="btn btn-sm icon-btn" disabled={page >= totalPages || loading} onClick={()=>onPage(totalPages)} aria-label="Last page">
          <ChevronsRightIcon/>
        </button>
      </nav>
    )}
  </div>
);

// Toolbar search for tables with a single text filter (no sidebar needed): Enter searches,
// × clears the search straight away
export const TableSearch = ({id, label, value, onChange, onSearch, onClear}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
  onClear: () => void;
}) => (
  <form className="dt-search" role="search" onSubmit={(e)=>{ e.preventDefault(); onSearch(); }}>
    <SearchIcon/>
    <label htmlFor={id} className="visually-hidden">{label}</label>
    <input
      id={id}
      type="text"
      className="dt-search-input"
      placeholder={label}
      value={value}
      onChange={(e)=>onChange(e.target.value)}
      autoComplete="off"
    />
    {value !== '' &&
      <button type="button" className="dt-search-clear" onClick={onClear} aria-label="Clear search">
        <CloseIcon/>
      </button>}
  </form>
);
